import { uuidv7 } from 'uuidv7';

const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

interface ApiErrorOptions {
  status: number | null;
  code: string;
  requestId?: string;
  details?: unknown;
  isNetworkError?: boolean;
  isTimeout?: boolean;
}

export class ApiError extends Error {
  public readonly status: number | null;
  public readonly code: string;
  public readonly requestId?: string;
  public readonly details?: unknown;
  public readonly isNetworkError: boolean;
  public readonly isTimeout: boolean;

  constructor(message: string, options: ApiErrorOptions) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.requestId = options.requestId;
    this.details = options.details;
    this.isNetworkError = options.isNetworkError ?? false;
    this.isTimeout = options.isTimeout ?? false;
  }
}

export class ServiceUnavailableError extends Error {
  public readonly requestId?: string;

  constructor(
    public readonly status: number,
    message?: string,
    requestId?: string,
  ) {
    super(message ?? `Service unavailable (${status})`);
    this.name = 'ServiceUnavailableError';
    this.requestId = requestId;
  }
}

export class UnauthorizedError extends Error {
  public readonly requestId?: string;

  constructor(message = 'Unauthorized', requestId?: string) {
    super(message);
    this.name = 'UnauthorizedError';
    this.requestId = requestId;
  }
}

type OnUnauthorized = () => void;

let unauthorizedHandler: OnUnauthorized | null = null;

/** Регистрирует обработчик 401 — вызывается при получении 401 от API. */
export function setUnauthorizedHandler(handler: OnUnauthorized): void {
  unauthorizedHandler = handler;
}

// Глобальный refresh lock: один активный refresh для всего приложения.
// Конкурентные 401 ждут этот Promise вместо запуска повторного refresh.
let refreshingPromise: Promise<boolean> | null = null;

/**
 * Вызывает POST /api/oauth/token с grant_type=refresh_token через raw fetch,
 * минуя request() во избежание рекурсии. Возвращает true при успехе.
 */
async function refreshToken(): Promise<boolean> {
  try {
    const resp = await fetch('/api/oauth/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ grant_type: 'refresh_token' }).toString(),
      credentials: 'include',
    });
    return resp.ok;
  } catch {
    return false;
  }
}

interface RequestOptions extends RequestInit {
  skipRefreshOn401?: boolean;
  timeoutMs?: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Элемент массива violations в теле ответа 422 (валидация API). */
export interface ApiViolation {
  propertyPath: string;
  message: string;
}

/**
 * Извлекает список нарушений валидации из payload ошибки (details у ApiError).
 */
export function parseApiViolations(details: unknown): ApiViolation[] {
  if (!isObject(details)) {
    return [];
  }
  const raw = details.violations;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: ApiViolation[] = [];
  for (const entry of raw) {
    if (!isObject(entry)) {
      continue;
    }
    const propertyPath = entry.propertyPath;
    const message = entry.message;
    if (typeof propertyPath !== 'string' || typeof message !== 'string') {
      continue;
    }
    const pathTrimmed = propertyPath.trim();
    const messageTrimmed = message.trim();
    if (!pathTrimmed || !messageTrimmed) {
      continue;
    }
    out.push({ propertyPath: pathTrimmed, message: messageTrimmed });
  }
  return out;
}

function extractMessage(payload: unknown): string | null {
  if (!isObject(payload)) {
    return null;
  }
  const keys = ['message', 'error', 'detail'] as const;
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }
  return null;
}

function extractCode(payload: unknown): string | null {
  if (!isObject(payload)) {
    return null;
  }
  const value = payload.code;
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }
  return null;
}

function codeByStatus(status: number): string {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 429:
      return 'too_many_requests';
    case 502:
      return 'bad_gateway';
    case 503:
      return 'service_unavailable';
    default:
      if (status >= 500) {
        return 'server_error';
      }
      return 'http_error';
  }
}

function createAbortError(signal: AbortSignal): Error {
  if (signal.reason instanceof Error) {
    return signal.reason;
  }
  const error = new Error('Request aborted');
  error.name = 'AbortError';
  return error;
}

function combineAbortSignals(
  primary: AbortSignal,
  secondary?: AbortSignal | null,
): AbortSignal {
  if (!secondary) {
    return primary;
  }
  const controller = new AbortController();
  const onAbort = (event: Event) => {
    const target = event.target as AbortSignal;
    controller.abort(createAbortError(target));
  };
  primary.addEventListener('abort', onAbort);
  secondary.addEventListener('abort', onAbort);
  return controller.signal;
}

function isTimeoutError(err: unknown): boolean {
  return err instanceof Error && err.name === 'TimeoutError';
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => {
    const timeoutError = new Error(`Request timeout after ${timeoutMs}ms`);
    timeoutError.name = 'TimeoutError';
    timeoutController.abort(timeoutError);
  }, timeoutMs);

  const signal = combineAbortSignals(timeoutController.signal, options.signal);
  try {
    return await fetch(url, {
      ...options,
      signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function toApiError(resp: Response): Promise<ApiError | UnauthorizedError | ServiceUnavailableError> {
  const requestId = resp.headers.get('X-Request-Id') ?? resp.headers.get('x-request-id') ?? undefined;
  const text = await resp.text();

  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  const message =
    extractMessage(payload) ??
    (typeof payload === 'string' && payload.trim() !== '' ? payload : null) ??
    `HTTP ${resp.status}`;
  const code = extractCode(payload) ?? codeByStatus(resp.status);

  if (resp.status === 401) {
    return new UnauthorizedError(message, requestId);
  }
  if (resp.status === 502 || resp.status === 503) {
    return new ServiceUnavailableError(resp.status, message, requestId);
  }
  return new ApiError(message, {
    status: resp.status,
    code,
    requestId,
    details: payload,
  });
}

async function parseJSONResponse<T>(resp: Response): Promise<T> {
  try {
    return (await resp.json()) as T;
  } catch (err) {
    throw new ApiError('Invalid JSON response', {
      status: resp.status,
      code: 'invalid_json_response',
      details: err,
    });
  }
}

async function request(url: string, options: RequestOptions = {}): Promise<Response> {
  const { skipRefreshOn401, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS, ...fetchOptions } = options;

  const headers = new Headers(fetchOptions.headers);
  headers.set('X-Request-Id', uuidv7());

  let resp: Response;
  try {
    resp = await fetchWithTimeout(
      url,
      {
        ...fetchOptions,
        headers,
        credentials: 'include',
      },
      timeoutMs,
    );
  } catch (err) {
    if (isTimeoutError(err)) {
      throw new ApiError('Request timeout', {
        status: null,
        code: 'timeout',
        details: err,
        isNetworkError: true,
        isTimeout: true,
      });
    }
    throw new ApiError('Network request failed', {
      status: null,
      code: 'network_error',
      details: err,
      isNetworkError: true,
    });
  }

  if (resp.status === 401) {
    if (!skipRefreshOn401) {
      // Запускаем refresh или присоединяемся к уже идущему.
      if (!refreshingPromise) {
        refreshingPromise = refreshToken().finally(() => {
          refreshingPromise = null;
        });
      }
      const refreshed = await refreshingPromise;

      if (refreshed) {
        return request(url, { ...options, skipRefreshOn401: true });
      }
    }

    if (unauthorizedHandler) {
      unauthorizedHandler();
    }
    throw new UnauthorizedError();
  }

  if (!resp.ok) {
    throw await toApiError(resp);
  }

  return resp;
}

export async function apiGet<T>(url: string): Promise<T> {
  const resp = await request(url);
  return parseJSONResponse<T>(resp);
}

export async function apiPost<T>(url: string, body: unknown): Promise<T> {
  const resp = await request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJSONResponse<T>(resp);
}

/** Отправляет POST с application/x-www-form-urlencoded (для /api/oauth/token). */
export async function apiPostForm<T>(url: string, params: Record<string, string>): Promise<T> {
  const resp = await request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(params).toString(),
  });
  return parseJSONResponse<T>(resp);
}

export async function apiPostEmpty(url: string): Promise<void> {
  await request(url, { method: 'POST' });
}

/** POST с JSON-телом, ожидает 204 No Content (без тела ответа). */
export async function apiPostNoContent(url: string, body: unknown): Promise<void> {
  const resp = await request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (resp.status !== 204) {
    throw new ApiError(`Unexpected response status: ${resp.status}`, {
      status: resp.status,
      code: 'unexpected_status',
    });
  }
}

/** Отправляет POST с multipart/form-data (для загрузки файлов). */
export async function apiPostFormData<T>(url: string, formData: FormData): Promise<T> {
  // Не устанавливаем Content-Type — fetch добавит boundary для FormData.
  const resp = await request(url, {
    method: 'POST',
    body: formData,
  });

  return parseJSONResponse<T>(resp);
}

export function toUserMessage(err: unknown): string {
  if (err instanceof UnauthorizedError) {
    return 'Сессия истекла. Выполните вход снова.';
  }
  if (err instanceof ServiceUnavailableError) {
    return 'Сервис временно недоступен. Попробуйте повторить позже.';
  }
  if (err instanceof ApiError) {
    if (err.isTimeout) {
      return 'Сервер не отвечает вовремя. Попробуйте снова.';
    }
    if (err.isNetworkError) {
      return 'Проблема с сетью. Проверьте соединение и повторите.';
    }
    switch (err.status) {
      case 400:
        return 'Некорректный запрос. Проверьте введенные данные.';
      case 422:
        return 'Данные не прошли проверку. Исправьте ошибки в форме.';
      case 403:
        return 'Недостаточно прав для выполнения действия.';
      case 404:
        return 'Запрошенные данные не найдены.';
      case 409:
        return 'Конфликт данных. Обновите страницу и повторите действие.';
      case 429:
        return 'Слишком много запросов. Повторите чуть позже.';
      default:
        if (err.status != null && err.status >= 500) {
          return 'Ошибка сервера. Попробуйте снова позже.';
        }
    }
    return err.message || 'Не удалось выполнить запрос.';
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Произошла ошибка. Попробуйте еще раз.';
}
